import { FC, JSX, useEffect, useRef, useState } from "react";
import { Link, RouteComponentProps, useHistory } from "react-router-dom"
import cx from "classnames";
import orderBy from "lodash/orderBy";

import { Breadcrumb, BreadcrumbTemplateEnum, Breadcrumbs, Page, PageHeader, PageHeaderRight, PageInner, PageSection } from "@app/shared";
import { ColumnConfig, SortDirections, Waffles, wafflesKeyColumnName, WafflesPaginationProps } from "@app/shared/waffles";
import { SortingMetadata } from "@app/shared/waffles/exports";
import { QESButton, QESCheckbox, QESSelect, QESTextInput } from "@app/shared/qes-standards";
import { DropdownItem, MultiselectDropdown } from "@app/shared/checkbox-dropdown";
import { RoleAssignmentModel, RoleModel, RoleRestricted, Roles, RoleService } from "@app/user";
import { QesIcons } from "@app/shared/icons/qesIcons";

interface RoleAssignmentTableData {
  [wafflesKeyColumnName]: string;
  clientId: number;
  clientName: string;
  userId: number;
  userName: JSX.Element;
  roleId: number;
  roleName: string;
  assignedBy: string;
  isDenied: JSX.Element;
}

const roleAssignmentColumnConfig: ColumnConfig = {
  assignedBy: {
    displayText: "Assigned By",
    noSort: true,
    testId: "assignedBy",
    wrapContent: true
  },
  clientId: {
    hidden: true,
    testId: "clientId",
  },
  clientName: {
    displayText: "Client Name",
    testId: "clientName",
  },
  isDenied: {
    displayText: "Is Denied",
    testId: "isDenied",
  },
  roleId: {
    hidden: true,
    testId: "roleId",
  },
  roleName: {
    displayText: "Role Name",
    testId: "roleName",
  },
  userId: {
    hidden: true,
    testId: "userId",
  },
  userName: {
    displayText: "User Name",
    testId: "userName",
  },
};

interface PageSettings {
  skip: number,
  sortDetails: SortingMetadata<RoleAssignmentTableData>,
}

const breadcrumbs: (string | Breadcrumb)[] = [
  {
    iconClass: "fa-list",
    text: "Role Assignments",
  }];

enum UserType {
  QuestAdmins = "QuestAdmins",
  QuestUsers = "QuestUsers",
  ClientUsers = "ClientUsers"
}

interface RoleAssignmentMatchProps {
  clientId?: string;
}

type RoleAssignmentProps = RouteComponentProps<RoleAssignmentMatchProps, undefined, undefined>;

export const RoleAssignments: FC<React.PropsWithChildren<RoleAssignmentProps>> = (props: RoleAssignmentProps) => {

  const roleService = new RoleService();
  const urlParams = new URLSearchParams(window.location.search);
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [take] = useState(100);
  const [roleAssignmentsTableData, setRoleAssignmentsTableData] = useState<RoleAssignmentTableData[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [numRecordsDisplayed, setNumRecordsDisplayed] = useState(0);
  const [clientNameFilter, setClientNameFilter] = useState(() => urlParams.get("client") ?? "");
  const [userNameFilter, setUserNameFilter] = useState(() => urlParams.get("user") ?? "");
  const [userTypeFilter, setUserTypeFilter] = useState<DropdownItem<UserType>[]>(() => getSelectedUserTypeItems(urlParams.get("userType")));
  const [defaultRoleFilterType] = useState("Inc");
  const [roleFilterType, setRoleFilterType] = useState(() => urlParams.get("roleFiltType") ?? defaultRoleFilterType);
  const [allRoles, setAllRoles] = useState<RoleModel[]>([]);
  const [roleFilter, setRoleFilter] = useState<DropdownItem<number>[]>([]);
  const [isFiltered, setIsFiltered] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const getQueryPageSettings = (): PageSettings => {
    let queryPageSettings: PageSettings = {
      skip: 0,
      sortDetails: {
        sortDirection: SortDirections.None,
        sortedColumn: null
      }
    }

    if (!!urlParams.get("pageSet"))
      queryPageSettings = JSON.parse(urlParams.get("pageSet"));

    return queryPageSettings;
  }
  const [pageSettings, setPageSettings] = useState<PageSettings>(getQueryPageSettings());
  //Used to block certain useEffect hooks from firing on the first run before we can apply the filtering, paging, etc... from the page's query string
  const firstRun = useRef(true);

  const paginationProps: WafflesPaginationProps = {
    changePage: (pageNumber: number) => setPageSettings({ ...pageSettings, skip: (pageNumber - 1) * take }),
    recordsPerPage: take,
    skip: pageSettings.skip,
    totalRecords: totalRecords,
  }

  useEffect(() => {
    fetchAllRoles();
  }, []);

  useEffect(() => {
    setRoleFilter(getSelectedRoleItems(urlParams.get("roles")));
  }, [allRoles]);

  useEffect(() => {
    if (firstRun.current && allRoles.length > 0)
      fetchRoleAssignments();
  }, [roleFilter]);

  useEffect(() => {
    if (firstRun.current || allRoles.length === 0)
      return;

    fetchRoleAssignments();
  }, [pageSettings]);

  const fetchAllRoles = async () => {
    setLoading(true);
    const roles = await roleService.getAllRoles();
    setAllRoles(orderBy(roles, (r) => r.name, "asc"));
    setLoading(false);
  }

  const fetchRoleAssignments = async () => {
    setLoading(true);

    let selectedUserTypes = userTypeFilter
    if (selectedUserTypes.length === 0) {
      selectedUserTypes = getAllUserTypeDropdownItems();
      setUserTypeFilter(selectedUserTypes);
    }

    const roleAssignmentList = await roleService.getRoleAssignments(
      pageSettings.skip,
      take,
      clientNameFilter,
      userNameFilter,
      selectedUserTypes.some(x => x.key === UserType.QuestAdmins),
      selectedUserTypes.some(x => x.key === UserType.QuestUsers),
      selectedUserTypes.some(x => x.key === UserType.ClientUsers),
      roleFilterType === defaultRoleFilterType,
      roleFilter.map(x => x.key),
      (!!props.match.params.clientId ? parseInt(props.match.params.clientId) : null),
      pageSettings.sortDetails.sortDirection,
      pageSettings.sortDetails.sortedColumn);

    setRoleAssignmentsTableData(createWafflesTable(roleAssignmentList.roleAssignments));
    setTotalRecords(roleAssignmentList.totalRoleAssignments);
    setNumRecordsDisplayed(roleAssignmentList.roleAssignments.length);

    const isUserTypeFiltered = getAllUserTypeDropdownItems().length !== selectedUserTypes.length;
    setIsFiltered(!!(clientNameFilter || userNameFilter || isUserTypeFiltered || roleFilter.length !== allRoles.length || roleFilterType !== defaultRoleFilterType));

    const searchParams = new URLSearchParams(window.location.search);

    if (pageSettings.skip > 0 || pageSettings.sortDetails.sortDirection !== SortDirections.None) {
      searchParams.set("pageSet", JSON.stringify(pageSettings));
    } else {
      searchParams.delete("pageSet");
    }
    if (!!clientNameFilter) {
      searchParams.set("client", clientNameFilter);
    } else {
      searchParams.delete("client");
    }
    if (!!userNameFilter) {
      searchParams.set("user", userNameFilter);
    } else {
      searchParams.delete("user");
    }
    if (isUserTypeFiltered) {
      searchParams.set("userType", JSON.stringify(selectedUserTypes));
    } else {
      searchParams.delete("userType");
    }
    if (roleFilter.length !== allRoles.length) {
      searchParams.set("roles", JSON.stringify(roleFilter));
    } else {
      searchParams.delete("roles");
    }
    if (roleFilterType !== defaultRoleFilterType) {
      searchParams.set("roleFiltType", JSON.stringify(roleFilterType));
    } else {
      searchParams.delete("roleFiltType");
    }
    history.replace(history.location.pathname + "?" + searchParams.toString());

    firstRun.current = false;
    setLoading(false);
  }

  const handleSort = (data: RoleAssignmentTableData[], newSortedColumn: keyof RoleAssignmentTableData, direction: SortDirections) => {
    setPageSettings({
      ...pageSettings,
      skip: 0,
      sortDetails: {
        sortDirection: direction,
        sortedColumn: newSortedColumn,
      }
    });
    return data;
  }

  const createWafflesTable = (data: RoleAssignmentModel[]) => {
    return data.map((rec) => {

      const userId = !rec.parentUserId ? rec.userId : rec.parentUserId;
      const userName = !rec.parentUserName ? rec.userName : rec.parentUserName;

      const userNameLink =
        <Link
          data-testid={`user-${userId}-link`}
          to={{
            pathname: `/admin/user-administration/user/${userId}`,
          }}
        >
          {userName}
        </Link>

      /* eslint-disable sort-keys */
      return ({
        key: `${rec.clientId}-${rec.userId}-${rec.roleId}`,
        clientId: rec.clientId,
        clientName: rec.clientName,
        userId: rec.userId,
        userName: userNameLink,
        roleId: rec.roleId,
        roleName: rec.roleName,
        assignedBy: rec.assignedBy,
        isDenied: (
          <QESCheckbox
            label=""
            testId="role-assignment__is-denied"
            disabled={true}
            checked={rec.isDenied}
            onChange={() => !rec.isDenied} />
        )
      });
      /* eslint-enable sort-keys */
    });
  }

  const getAllRoleDropdownItems = () => {
    return allRoles.map(x => {
      return { key: x.roleId, name: x.name }
    });
  }

  const getSelectedRoleItems = (selectedKeysJson: string) => {
    if (!selectedKeysJson)
      return getAllRoleDropdownItems();

    const selectedKeys = JSON.parse(selectedKeysJson);

    if (Array.isArray(selectedKeys))
      return getAllRoleDropdownItems().filter(item => selectedKeys.some(x => x.key === item.key));
    else
      return [];
  }

  const handleSearchKeyUp = (e: React.KeyboardEvent) => {
    if (e.key.toLowerCase() === "enter") {
      handleSearch();
    }
  }

  const handleSearch = () => {
    setPageSettings({ ...pageSettings, skip: 0 });
  }

  const handleClear = () => {
    setClientNameFilter("");
    setUserNameFilter("");
    setUserTypeFilter(getAllUserTypeDropdownItems());
    setRoleFilter(getAllRoleDropdownItems());
    setRoleFilterType(defaultRoleFilterType);

    setPageSettings({ ...pageSettings, skip: 0 });
  }

  const handleDownload = async () => {
    setIsDownloading(true);

    const curPageSettings = getQueryPageSettings()
    const curUserTypes = getSelectedUserTypeItems(urlParams.get("userType"));

    await roleService.getRoleAssignmentsCsv(
      urlParams.get("client") ?? "",
      urlParams.get("user") ?? "",
      curUserTypes.some(x => x.key === UserType.QuestAdmins),
      curUserTypes.some(x => x.key === UserType.QuestUsers),
      curUserTypes.some(x => x.key === UserType.ClientUsers),
      (urlParams.get("roleFiltType") ?? defaultRoleFilterType) === defaultRoleFilterType,
      getSelectedRoleItems(urlParams.get("roles")).map(x => x.key),
      (!!props.match.params.clientId ? parseInt(props.match.params.clientId) : null),
      curPageSettings.sortDetails.sortDirection,
      curPageSettings.sortDetails.sortedColumn);

    setIsDownloading(false);
  }

  return (
    <>
      {(
        <Page documentTitle="Role Assignments - Admin">
          <Breadcrumbs template={BreadcrumbTemplateEnum.InternalReports} items={breadcrumbs} />
          <PageInner>
            <PageHeader title="Role Assignments" iconName={QesIcons.FontAwesome.List}>
              <PageHeaderRight>
                <QESButton
                  className={cx({
                    "downloading": isDownloading,
                  })}
                  disabled={loading || isDownloading}
                  onClick={() => handleDownload()}
                  qesType="primary"
                  qesTestId="role-assigments__download"
                >
                  <i className="fa fa-download pr5" />Download
                </QESButton>
              </PageHeaderRight>
            </PageHeader>
            <PageSection>
              <div className="role-assignments-filters mb-3 mt-5">
                <RoleRestricted allowedRoles={[Roles.QUEST_ADMIN]}>
                  {!props.match.params.clientId ?
                    <QESTextInput containerClassName="role-assignments-filters__input" label="Client Name" testId="role-assignments-filter__client-name" value={clientNameFilter} onKeyUp={(e) => handleSearchKeyUp(e)} onChange={(e) => setClientNameFilter(e.target.value)} disabled={loading} />
                    : null
                  }
                </RoleRestricted>
                <QESTextInput containerClassName="role-assignments-filters__input" label="User Name" testId="role-assignments-filter__user-name" value={userNameFilter} onKeyUp={(e) => handleSearchKeyUp(e)} onChange={(e) => setUserNameFilter(e.target.value)} disabled={loading} />
                <QESSelect
                  allowEmpty={false}
                  containerClassName="role-assignments-filters__input"
                  disabled={loading}
                  label="Role Filter Type"
                  onChange={(e) => setRoleFilterType(e.target.value)}
                  testId="role-assignments__roles-filter-type"
                  value={roleFilterType}
                >
                  <option value="Inc">Include Selected Roles</option>
                  <option value="Exc">Exclude Selected Roles</option>
                </QESSelect>
                <MultiselectDropdown
                  containerClassName="role-assignments-filters__input"
                  label="Selected Roles"
                  disabled={false}
                  items={getAllRoleDropdownItems()}
                  onSelectionChanged={(selectedItems: DropdownItem<number>[]) => { setRoleFilter(selectedItems); }}
                  selectedItems={roleFilter}
                  testId="role-assignments__roles-filter"
                />
                <MultiselectDropdown
                  containerClassName="role-assignments-filters__input"
                  label="User Types"
                  disabled={false}
                  hideSearch={true}
                  items={getAllUserTypeDropdownItems()}
                  onSelectionChanged={(selectedItems: DropdownItem<UserType>[]) => { setUserTypeFilter(selectedItems); }}
                  selectedItems={userTypeFilter}
                  testId="role-assignments__user-type filter"
                />
                <QESButton qesType="primary" qesTestId="role-assignments__search-button" onClick={handleSearch} disabled={loading}>Search</QESButton>
                <QESButton qesType="secondary" qesTestId="role-assignments__clear-filters" onClick={handleClear} disabled={!isFiltered || loading}>Clear</QESButton>
              </div>
              <p>Displaying {numRecordsDisplayed} of {totalRecords} role assignments {isFiltered ? "(filtered)" : ""}</p>
              <Waffles<RoleAssignmentTableData>
                columns={roleAssignmentColumnConfig}
                customSort={handleSort}
                data={roleAssignmentsTableData}
                enablePagination={true}
                fullWidth={true}
                loading={loading}
                passThroughSorting={true}
                paginationProps={paginationProps}
                testId="role-assignments-waffles-table"
              />
            </PageSection>
          </PageInner>
        </Page>
      )}
    </>
  );
}

const getAllUserTypeDropdownItems = () => {
  const items: DropdownItem<UserType>[] = [];
  items.push({ key: UserType.QuestAdmins, name: "Quest Admins" });
  items.push({ key: UserType.QuestUsers, name: "Quest Users" });
  items.push({ key: UserType.ClientUsers, name: "Client Users" });

  return items;
}

const getSelectedUserTypeItems = (selectedKeysJson: string) => {
  if (!selectedKeysJson)
    return getAllUserTypeDropdownItems();

  const selectedKeys = JSON.parse(selectedKeysJson);

  if (Array.isArray(selectedKeys) && selectedKeys.length > 0)
    return getAllUserTypeDropdownItems().filter(item => selectedKeys.some(x => x.key === item.key));
  else
    return getAllUserTypeDropdownItems();
}